/**
 * Text processing utilities for canvas text elements
 * Extracted from AddCanvasTemplate.js to reduce file size
 */

/**
 * Calculate the width needed for a text block based on its content
 * @param {Object} text - Text object with partsByType, task, note properties
 * @returns {number} Calculated width in pixels
 */
export const calculateTextBlockWidth = (text) => {
  let maxLineLength = 0;

  // Estimate character width
  const approxCharWidth = 8;

  // Parts
  if (text.partsByType) {
    for (const [typeName, typeData] of Object.entries(text.partsByType)) {
      if (!typeData.parts || typeData.parts.length === 0) continue;

      maxLineLength = Math.max(maxLineLength, typeName.length);

      typeData.parts.forEach((partData) => {
        // Skip null or undefined partData entries
        if (!partData) return;
        
        const { part, qty, unit } = partData;
        const line = `${part} - Qty-${qty} ${unit}`;
        maxLineLength = Math.max(maxLineLength, line.length);
      });
    }
  }

  // Task
  if (text.task) {
    maxLineLength = Math.max(maxLineLength, text.task.length);
  }

  // Note
  if (text.note) {
    maxLineLength = Math.max(maxLineLength, text.note.length);
  }

  return maxLineLength * approxCharWidth + 40; // add padding
};

/**
 * Convert text element to API payload format
 * @param {Object} text - Text element object
 * @returns {Object} Formatted payload with text_box_content_data and text_box_properties
 */
const resolveTextBoxId = (text = {}) => {
  const candidates = [
    text?.text_box_id,
    text?.textboxId,
    text?.textBoxId,
    text?.canvas_text_box_id,
    text?.canvasTextBoxId,
    text?.shape_info?.text_box_id,
    text?.shape_properties?.text_box_id,
    text?.text_box_properties?.text_box_id,
  ]
    .map((value) =>
      value === undefined || value === null ? null : String(value).trim()
    )
    .filter((value) => value);

  return candidates.length > 0 ? candidates[0] : null;
};

const withTextBoxId = (items = [], fallbackId = null) => {
  if (!fallbackId) {
    return items.map((item) => ({ ...item }));
  }

  return items.map((item) => {
    const cloned = { ...item };
    if (!cloned.text_box_id || cloned.text_box_id === "") {
      cloned.text_box_id = fallbackId;
    }
    return cloned;
  });
};

const mergeTextBoxContentData = (baseEntries = [], newEntries = []) => {
  const merged = (Array.isArray(baseEntries) ? baseEntries : [])
    .filter((entry) => entry && typeof entry === "object")
    .map((entry) => ({ ...entry }));

  newEntries.forEach((entry) => {
    if (!entry) return;
    const normalizedEntry = { ...entry };

    const matchById =
      normalizedEntry.text_box_content_id &&
      merged.findIndex(
        (existing) =>
          existing.text_box_content_id === normalizedEntry.text_box_content_id
      );

    if (matchById !== -1 && merged[matchById]) {
      const existing = merged[matchById];
      merged[matchById] = {
        ...existing,
        ...normalizedEntry,
        text_box_content_id: existing.text_box_content_id,
      };
      return;
    }

    // Match by structure: part_ref + text_box_id + qty (all must match to merge)
    // This allows the same part_ref to appear multiple times with different qty values
    const matchByStructure = merged.findIndex((existing) => {
      if (!normalizedEntry.part_ref || !existing.part_ref) return false;
      if (
        normalizedEntry.text_box_id &&
        existing.text_box_id &&
        normalizedEntry.text_box_id !== existing.text_box_id
      ) {
        return false;
      }
      // Include qty in the match - same part_ref with different qty should be separate entries
      const existingQty = existing.qty ?? 0;
      const newQty = normalizedEntry.qty ?? 0;
      return (
        existing.part_ref === normalizedEntry.part_ref &&
        existingQty === newQty
      );
    });

    if (matchByStructure !== -1 && merged[matchByStructure]) {
      const existing = merged[matchByStructure];
      merged[matchByStructure] = {
        ...existing,
        ...normalizedEntry,
        text_box_content_id: existing.text_box_content_id,
      };
      return;
    }

    // Match by part_ref + qty (both must match to merge)
    // This ensures same part_ref with different qty values are kept separate
    const matchByPartRefAndQty = merged.findIndex(
      (existing) => {
        if (!existing.part_ref || !normalizedEntry.part_ref) return false;
        const existingQty = existing.qty ?? 0;
        const newQty = normalizedEntry.qty ?? 0;
        return (
          existing.part_ref === normalizedEntry.part_ref &&
          existingQty === newQty
        );
      }
    );

    if (matchByPartRefAndQty !== -1 && merged[matchByPartRefAndQty]) {
      const existing = merged[matchByPartRefAndQty];
      merged[matchByPartRefAndQty] = {
        ...existing,
        ...normalizedEntry,
        text_box_content_id: existing.text_box_content_id,
      };
    } else {
      // No match found - add as a new entry (allows same part_ref with different qty)
      merged.push(normalizedEntry);
    }
  });

  return merged;
};

export const convertTextToPayload = (text) => {
  const resolvedPartsByType =
    (text?.partsByType && typeof text.partsByType === "object"
      ? text.partsByType
      : null) ||
    (text?.shape_info?.partsByType &&
    typeof text.shape_info.partsByType === "object"
      ? text.shape_info.partsByType
      : null) ||
    (text?.shape_properties?.partsByType &&
    typeof text.shape_properties.partsByType === "object"
      ? text.shape_properties.partsByType
      : {});

  const resolvedTextBoxContentDataSource = Array.isArray(
    text?.text_box_content_data
  )
    ? text.text_box_content_data
    : Array.isArray(text?.shape_info?.text_box_content_data)
    ? text.shape_info.text_box_content_data
    : Array.isArray(text?.shape_properties?.text_box_content_data)
    ? text.shape_properties.text_box_content_data
    : [];

  // Extract parts from the existing format
  const parts = (resolvedPartsByType?.Part?.parts || []).filter(
    (p) => p.part_ref && p.qty && parseFloat(p.qty) > 0 && p.unit
  );

  // Extract text box content data from the new format
  const resolvedTextBoxId = resolveTextBoxId(text);
  const textBoxContentData = withTextBoxId(
    resolvedTextBoxContentDataSource || [],
    resolvedTextBoxId
  );

  // Enhanced partsByType processing
  let enhancedTextBoxData = [];
  if (resolvedPartsByType && typeof resolvedPartsByType === "object") {
    // Iterate through all types in partsByType
    Object.keys(resolvedPartsByType).forEach((typeKey) => {
      const typeData = resolvedPartsByType[typeKey];
      if (typeData && typeData.parts && Array.isArray(typeData.parts)) {
        const textBoxData = typeData.parts
          .map((part) => {
            const item = {
              part_ref: part.part_ref,
              inline_note: part.notePlainText || part.note || "",
              qty: parseInt(part.qty) || 0,
              unit_id: part.units || part.unit_id || null,
            };

            // Only add units if it's not empty or null
            const units = part.units || part.unit_id || part.unit;
            if (units && units !== null && units !== "") {
              item.units = units;
            }

            return item;
          })
          .filter((part) => part.part_ref);

        if (textBoxData.length > 0) {
          enhancedTextBoxData.push(
            ...withTextBoxId(textBoxData, resolvedTextBoxId)
          );
        }
      }
    });
  }

  // Combine all sources of text box content data
  const generatedEntries = [
    ...withTextBoxId(
      parts.map((part) => {
        const item = {
          part_ref: part.part_ref,
          inline_note: part.notePlainText || part.note || "",
          qty: parseInt(part.qty),
          unit_id: part.unit_id || null,
        };

        // Only add units if it's not empty or null
        if (part.units && part.units !== null && part.units !== "") {
          item.units = part.units;
        }

        return item;
      }),
      resolvedTextBoxId
    ),
    ...enhancedTextBoxData,
  ];

  const text_box_content_data = mergeTextBoxContentData(
    textBoxContentData,
    generatedEntries
  );

  const text_box_properties = {
    text_box_properties: {
      fontSize: text?.textStyles?.fontSize || 14,
      color: text?.textStyles?.color || "#000000",
    },
    note: text?.notePlainText || text?.note || "",
    task: text?.taskPlainText || text?.task || "",
    canvas_page_location_and_size: {
      x: text.x ?? 0,
      y: text.y ?? 0,
      width: text.width ?? 200,
      height: text.height ?? 100,
      rotation: text.rotation ?? 0,
    },
  };

  return {
    text_box_content_data,
    text_box_properties,
  };
};
